import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { GraduationCap, MagnifyingGlass, MapPin, X, Buildings, PlusCircle } from "@phosphor-icons/react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

// Groups a flat saved list into Country → State → City → [institutions]
function groupByLocation(items) {
  const tree = {};
  for (const it of items) {
    const country = it.country || "Other";
    const state = it.state || "—";
    const city = it.city || "—";
    tree[country] = tree[country] || {};
    tree[country][state] = tree[country][state] || {};
    tree[country][state][city] = tree[country][state][city] || [];
    tree[country][state][city].push(it);
  }
  return tree;
}

export default function SavedInstitutions({ profile, isMe, onChange }) {
  const [query, setQuery] = useState("");
  const [addQuery, setAddQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [busyId, setBusyId] = useState("");
  const saved = profile.saved_institutions || [];
  const savedIds = useMemo(() => new Set(saved.map((s) => s.institution_id)), [saved]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return saved;
    return saved.filter((s) => [s.name, s.country, s.state, s.city, s.type].filter(Boolean).some((v) => v.toLowerCase().includes(q)));
  }, [saved, query]);

  const tree = useMemo(() => groupByLocation(filtered), [filtered]);

  const runSearch = async (val) => {
    setAddQuery(val);
    if (!val.trim()) { setResults([]); return; }
    setSearching(true);
    try {
      const { data } = await api.get("/institutions", { params: { q: val.trim(), limit: 8 } });
      setResults(data.results || []);
    } catch { setResults([]); }
    finally { setSearching(false); }
  };

  const save = async (inst) => {
    setBusyId(inst.id);
    try { await api.post("/profiles/me/saved-institutions", { institution_id: inst.id }); toast.success(`Saved ${inst.name}`); onChange && onChange(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save"); }
    finally { setBusyId(""); }
  };
  const remove = async (id) => {
    setBusyId(id);
    try { await api.delete(`/profiles/me/saved-institutions/${id}`); toast.success("Removed"); onChange && onChange(); }
    catch { toast.error("Could not remove"); }
    finally { setBusyId(""); }
  };

  return (
    <Card className="border-border/60 p-6 shadow-sm" data-testid="saved-institutions">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 font-heading text-xl font-medium">
          <GraduationCap className="text-secondary" size={22} weight="duotone" /> {isMe ? "My schools & colleges" : "Schools & colleges"}
        </h2>
        <Badge variant="secondary" className="rounded-full font-normal">{saved.length}</Badge>
      </div>

      {isMe && (
        <div className="mt-4 rounded-xl border border-border/60 bg-muted/30 p-3" data-testid="saved-add-box">
          <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-muted-foreground"><PlusCircle size={13} /> Add an institution to your profile</p>
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={addQuery} onChange={(e) => runSearch(e.target.value)} placeholder="Search any school, college or university…" className="rounded-full pl-9" data-testid="saved-add-search" />
          </div>
          {addQuery.trim() && (
            <div className="mt-2 space-y-1.5">
              {searching && <p className="text-xs text-muted-foreground">Searching…</p>}
              {!searching && results.length === 0 && <p className="text-xs text-muted-foreground">No matches. <Link to="/institutions/suggest" className="text-secondary hover:underline">Suggest it</Link>.</p>}
              {results.map((r) => (
                <div key={r.id} className="flex items-center justify-between gap-2 rounded-lg bg-background px-3 py-2 text-sm">
                  <span className="min-w-0 truncate">{r.name} <span className="text-muted-foreground">· {[r.location, r.country].filter(Boolean).join(", ")}</span></span>
                  {savedIds.has(r.id)
                    ? <Badge variant="outline" className="shrink-0 rounded-full text-xs font-normal">Saved</Badge>
                    : <Button size="sm" variant="ghost" className="h-7 shrink-0 rounded-full text-xs" onClick={() => save(r)} disabled={busyId === r.id} data-testid={`saved-add-${r.id}`}>Add</Button>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {saved.length > 0 && (
        <div className="relative mt-4">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name, country, state or city…" className="rounded-full pl-9" data-testid="saved-search" />
        </div>
      )}

      <div className="mt-5 space-y-5" data-testid="saved-list">
        {saved.length === 0 && <p className="text-sm text-muted-foreground">{isMe ? "You haven't saved any institutions yet. Use the search above or the “Save to my profile” button in the directory." : "No saved institutions yet."}</p>}
        {saved.length > 0 && filtered.length === 0 && <p className="text-sm text-muted-foreground">No saved institutions match “{query}”.</p>}
        {Object.keys(tree).sort().map((country) => (
          <div key={country} data-testid={`saved-country-${country}`}>
            <div className="flex items-center gap-1.5 text-sm font-semibold"><MapPin size={14} className="text-secondary" /> {country}</div>
            <div className="mt-2 space-y-3 border-l-2 border-border/60 pl-4">
              {Object.keys(tree[country]).sort().map((state) => (
                <div key={state}>
                  {state !== "—" && <div className="text-xs font-medium text-muted-foreground">{state}</div>}
                  <div className="mt-1 space-y-2">
                    {Object.keys(tree[country][state]).sort().map((city) => (
                      <div key={city}>
                        {city !== "—" && <div className="text-[11px] uppercase tracking-wide text-muted-foreground/70">{city}</div>}
                        <div className="mt-1 flex flex-wrap gap-2">
                          {tree[country][state][city].map((it) => (
                            <div key={it.institution_id} className="group inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-card py-1 pl-3 pr-1.5 text-sm transition-colors hover:border-secondary/40" data-testid={`saved-item-${it.institution_id}`}>
                              <Link to={`/institutions/${it.institution_id}`} className="inline-flex items-center gap-1.5 hover:text-secondary"><Buildings size={13} /> {it.name}</Link>
                              {isMe && <button onClick={() => remove(it.institution_id)} disabled={busyId === it.institution_id} className="grid h-5 w-5 place-items-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground" data-testid={`saved-remove-${it.institution_id}`}><X size={12} /></button>}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
